import { apiPost, apiGet } from './client';

export interface CustomerSession {
  token: string;
  expiresAt: string;
}

export interface CustomerInfo {
  id: string;
  email: string;
  businessName: string | null;
  planTier: string | null;
  billingFrequency: string | null;
  hasBillingAccount: boolean;
  websiteId: string | null;
  websiteStatus: string | null;
  websiteUrl: string | null;
  customDomain?: string | null;
  /** Per-customer portal feature flags, from /customer/me. */
  features?: { galleryEditing?: boolean };
}

interface SendMagicLinkResponse {
  success: boolean;
  message: string;
}

interface VerifyMagicLinkResponse {
  success: boolean;
  session: CustomerSession;
  customer: CustomerInfo;
}

interface GetSessionResponse {
  customer: CustomerInfo;
  session: {
    expiresAt: string;
  };
}

export async function sendMagicLink(email: string): Promise<SendMagicLinkResponse> {
  return apiPost<SendMagicLinkResponse>('/api/customer/auth/send-magic-link', { email });
}

export async function verifyMagicLink(token: string): Promise<VerifyMagicLinkResponse> {
  return apiPost<VerifyMagicLinkResponse>('/api/customer/auth/verify', { token });
}

export async function getSession(): Promise<GetSessionResponse> {
  return apiGet<GetSessionResponse>('/api/customer/auth/session');
}

export async function logout(): Promise<void> {
  await apiPost('/api/customer/auth/logout');
}

// Session storage helpers
const SESSION_KEY = 'niftee_portal_session';
const CUSTOMER_KEY = 'niftee_portal_customer';

export function saveSession(session: CustomerSession, customer: CustomerInfo): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer));
}

export function getStoredSession(): CustomerSession | null {
  const stored = localStorage.getItem(SESSION_KEY);
  if (!stored) return null;
  try {
    const session = JSON.parse(stored) as CustomerSession;
    // Check if expired
    if (new Date(session.expiresAt) < new Date()) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function getStoredCustomer(): CustomerInfo | null {
  const stored = localStorage.getItem(CUSTOMER_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as CustomerInfo;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(CUSTOMER_KEY);
}
